import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Pencil, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAdminApi, downloadCsv } from "./useAdminApi";
import { errMsg, money } from "@/lib/api";

const DISPLAY = { public_amount: "Name + amount", name_only: "Name only", anonymous: "Anonymous" };
const LABEL = { donation: "donor", sponsorship: "sponsor", ticket: "ticket sale" };
const METHODS = ["In person", "Cash", "Check", "Bank transfer", "Other online", "Other"];
const today = () => new Date().toISOString().slice(0, 10);

const ManualDialog = ({ kind, edit, onClose, onSaved }) => {
  const api = useAdminApi();
  const [f, setF] = useState(null);
  const [refs, setRefs] = useState([]);
  useEffect(() => {
    if (kind !== "donation") api("get", `/admin/c/${kind === "ticket" ? "ticket_tiers" : "sponsorships"}`).then(setRefs);
  }, [api, kind]);
  useEffect(() => {
    if (edit) setF(edit.id ? { ...edit, date: edit.created_at.slice(0, 10) } : { kind, name: "", email: "", organization: "", amount: "", quantity: 1, ref_name: "", method: "In person", date: today(), display: kind === "ticket" ? "anonymous" : "public_amount", message: "", recurring: false, approved: kind !== "ticket" });
  }, [edit, kind]);
  if (!edit || !f) return null;
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async (e) => {
    e.preventDefault();
    const body = { kind, name: f.name, email: f.email, organization: f.organization || "", amount: Number(f.amount), quantity: Number(f.quantity) || 1, ref_id: f.ref_id || "", ref_name: f.ref_name || "", method: f.method || "In person", date: f.date, display: f.display, message: f.message || "", recurring: !!f.recurring, approved: !!f.approved };
    try {
      await api(edit.id ? "put" : "post", edit.id ? `/admin/transactions/${edit.id}` : "/admin/transactions", body);
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const inp = (k, label, props = {}) => (
    <label className="block"><span className="text-xs font-semibold block mb-1">{label}</span>
      <input className="field" value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`manual-${k}-input`} {...props} /></label>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="admin-scope bg-mas-cream text-ink max-w-lg max-h-[90vh] overflow-y-auto" data-testid="manual-tx-dialog">
        <DialogHeader><DialogTitle className="text-2xl">{edit.id ? "Edit" : "Add"} {LABEL[kind]}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {inp("name", "Name", { required: kind !== "donation" })}
            {inp("email", "Email (optional)", { type: "email" })}
            {inp("amount", "Total amount ($)", { type: "number", min: "0.01", step: "0.01", required: true })}
            {inp("date", "Date", { type: "date", required: true })}
            {kind !== "donation" && (
              <label className="block"><span className="text-xs font-semibold block mb-1">{kind === "ticket" ? "Ticket tier" : "Package"}</span>
                <select className="field" value={f.ref_id || ""} data-testid="manual-ref-select"
                  onChange={(e) => { const r = refs.find((x) => x.id === e.target.value); setF({ ...f, ref_id: e.target.value, ref_name: r?.name || "", amount: f.amount || (r ? r.price * (Number(f.quantity) || 1) : "") }); }}>
                  <option value="">Other / none</option>
                  {refs.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select></label>
            )}
            {kind === "ticket" && inp("quantity", "Quantity", { type: "number", min: "1" })}
            {kind === "sponsorship" && inp("organization", "Organization")}
            <label className="block"><span className="text-xs font-semibold block mb-1">How they paid</span>
              <select className="field" value={f.method} onChange={(e) => set("method", e.target.value)} data-testid="manual-method-select">{METHODS.map((m) => <option key={m}>{m}</option>)}</select></label>
          </div>
          {kind !== "ticket" && (
            <>
              <label className="block"><span className="text-xs font-semibold block mb-1">Donor Wall display</span>
                <select className="field" value={f.display} onChange={(e) => set("display", e.target.value)} data-testid="manual-display-select">{Object.entries(DISPLAY).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
              <label className="block"><span className="text-xs font-semibold block mb-1">Message (optional)</span>
                <textarea rows={2} className="field" value={f.message} onChange={(e) => set("message", e.target.value)} data-testid="manual-message-input" /></label>
              <div className="flex gap-6 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.approved} onChange={(e) => set("approved", e.target.checked)} className="w-4 h-4 accent-[#245E55]" data-testid="manual-approved-toggle" />Show on Donor Wall</label>
                {kind === "donation" && <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.recurring} onChange={(e) => set("recurring", e.target.checked)} className="w-4 h-4 accent-[#245E55]" data-testid="manual-recurring-toggle" />Monthly donor</label>}
              </div>
            </>
          )}
          <p className="text-xs">Counts toward the campaign total{kind === "ticket" ? " (tickets are reported separately)" : ""}.</p>
          <button className="btn-terra w-full" data-testid="manual-save-button">Save</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminMoney({ kind }) {
  const api = useAdminApi();
  const [rows, setRows] = useState(null);
  const [pending, setPending] = useState(false);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api("get", `/admin/transactions?kind=${kind}&include_pending=${pending}`).then(setRows), [api, kind, pending]);
  useEffect(() => {
    load();
  }, [load]);
  const wall = kind !== "ticket";

  const patch = async (id, body) => {
    try {
      await api("patch", `/admin/transactions/${id}`, body);
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async (r) => {
    if (!window.confirm("Delete this record?")) return;
    await api("delete", `/admin/transactions/${r.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid={`admin-money-${kind}`}>
      <div className="flex flex-wrap gap-3 items-center mb-5">
        <button onClick={() => setEdit({})} className="btn-terra !py-2" data-testid={`admin-add-manual-${kind}-button`}><Plus className="w-4 h-4" />Add {LABEL[kind]} (offline / in person)</button>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={pending} onChange={(e) => setPending(e.target.checked)} className="accent-[#ED773C]" data-testid="admin-show-pending-toggle" />Include unfinished checkouts</label>
        <button onClick={() => downloadCsv(api, kind)} className="btn-ink !py-2 ml-auto" data-testid={`admin-export-${kind}-button`}><Download className="w-4 h-4" />Export CSV</button>
      </div>
      {wall && <p className="text-sm mb-4">Turn on “On wall” to show a gift on the public Donor Wall. Donors' display choice is respected (you can change it). Records you add by hand can be edited or deleted.</p>}
      <div className="bg-white border-2 border-mas-ink overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-mas-paper text-left"><tr>
            <th className="p-3">Date</th><th className="p-3">Name / email</th><th className="p-3">{kind === "ticket" ? "Tier × qty" : kind === "sponsorship" ? "Package" : "Type"}</th><th className="p-3">Amount</th><th className="p-3">Status</th>
            {wall && <><th className="p-3">Display</th><th className="p-3">Message</th><th className="p-3">On wall</th></>}
            <th className="p-3" />
          </tr></thead>
          <tbody>
            {rows?.length === 0 && <tr><td colSpan={9} className="p-6">No records yet.</td></tr>}
            {rows?.map((r) => (
              <tr key={r.id} className="border-t border-mas-sand align-top" data-testid={`admin-tx-${r.id}`}>
                <td className="p-3 whitespace-nowrap">{r.created_at.slice(0, 10)}</td>
                <td className="p-3"><p className="font-semibold">{r.name || "—"}</p><p className="text-xs">{r.email}</p>{r.organization && <p className="text-xs">{r.organization}</p>}</td>
                <td className="p-3">{kind === "ticket" ? `${r.ref_name || "—"} × ${r.quantity}` : kind === "sponsorship" ? r.ref_name || "—" : r.recurring ? (r.renewal ? "Monthly renewal" : "Monthly") : "One-time"}</td>
                <td className="p-3 font-semibold">{money(r.amount, true)}</td>
                <td className="p-3"><span className={`font-mono text-[10px] uppercase px-2 py-0.5 ${r.payment_status === "paid" ? "bg-aqua" : "bg-mas-paper2"}`}>{r.manual ? r.method || "offline" : r.payment_status}</span></td>
                {wall && <>
                  <td className="p-3"><select className="field !py-1 !text-xs" value={r.display} onChange={(e) => patch(r.id, { display: e.target.value })} data-testid={`admin-tx-display-${r.id}`}>{Object.entries(DISPLAY).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></td>
                  <td className="p-3 max-w-[220px] text-xs italic">{r.message || "—"}</td>
                  <td className="p-3">{r.renewal ? "—" : <input type="checkbox" disabled={r.payment_status !== "paid"} checked={!!r.approved} onChange={(e) => patch(r.id, { approved: e.target.checked })} className="w-5 h-5 accent-[#245E55]" data-testid={`admin-tx-approve-${r.id}`} />}</td>
                </>}
                <td className="p-3 whitespace-nowrap">{r.manual && <>
                  <button onClick={() => setEdit(r)} className="p-1.5" data-testid={`admin-tx-edit-${r.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(r)} className="p-1.5 text-passion" data-testid={`admin-tx-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                </>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ManualDialog kind={kind} edit={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
